import type { HTMLAttributes, ReactElement } from 'react';

import classNames from 'classnames';

import styles from './ConnectCards.module.css';

import type { BoxProps } from '../Box';
import { Box } from '../Box';
import type { CardProps } from '../Card';
import { ConnectCard } from './ConnectCard';

export interface ConnectCardsProps
  extends
    HTMLAttributes<HTMLElement>,
    Pick<BoxProps, 'm' | 'mt' | 'mb' | 'mx' | 'ml' | 'mr'> {
  as?: 'div' | 'section';
  children: Array<ReactElement<CardProps, typeof ConnectCard>>;
}

/**
 * Connect multiple {@link Card} components together with a line and dots.
 *
 * Use this component to wrap multiple `ConnectCards.Card` components. To add
 * the dots, use the `data-connect-to-card` attribute on the child components.
 *
 * ```tsx
 * <ConnectCards>
 *   <ConnectCards.Card>
 *     <Text data-connect-to-card>Hello</Text>
 *   </ConnectCards.Card>
 *   <ConnectCards.Card>
 *     <Text data-connect-to-card>World</Text>
 *   </ConnectCards.Card>
 * </ConnectCards>;
 * ```
 *
 * @see
 * https://www.figma.com/design/hFiXhNtTdoWrNk1DqQMEWJ/branch/EPN1PiZSc16jrwrchnCaVm/Design-System-Library?m=auto&node-id=14130-4411
 *
 * @see https://corgi-x.tfd.engineering/components/connectedcards
 */
export function ConnectCards({ children, ...props }: ConnectCardsProps) {
  return (
    <Box
      {...props}
      className={classNames(styles.connectCards, props.className)}
    >
      {children}
    </Box>
  );
}

ConnectCards.Card = ConnectCard;
